import { getProjectRepository } from "../data/projectRepository";
import { ApiError, apiErrorFromResponse } from "./errors";
import { statelessRequest } from "./statelessClient";

export { ApiError } from "./errors";

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const documentRequest = parseDocumentRequest(path, init);
  if (documentRequest) {
    const repository = await getProjectRepository();
    return repository.execute<T>(
      documentRequest.projectId,
      documentRequest.commandType,
      documentRequest.payload,
      init?.signal ?? undefined
    );
  }
  if (path === "/mock/teachers" || path === "/mock/classes") {
    return statelessRequest<T>(path, init?.signal ?? undefined);
  }
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...init?.headers
    }
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export async function apiDownload(
  path: string,
  payload: unknown
): Promise<void> {
  const exportMatch = path.match(/^\/projects\/([0-9a-f-]+)\/exports$/i);
  if (exportMatch?.[1]) {
    if (typeof payload !== "object" || payload === null || Array.isArray(payload)) {
      throw new Error("导出请求必须是 JSON 对象");
    }
    const repository = await getProjectRepository();
    const exportPayload = { ...(payload as Record<string, unknown>) };
    const versionId =
      typeof exportPayload.__timetable_version_id === "string"
        ? exportPayload.__timetable_version_id
        : null;
    delete exportPayload.__timetable_version_id;
    const result = versionId
      ? await repository.exportTimetableVersion(
          exportMatch[1],
          versionId,
          exportPayload
        )
      : await repository.export(exportMatch[1], exportPayload);
    triggerDownload(result.blob, result.filename);
    return;
  }
  const response = await fetch(`/api/v1${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const filename = encodedName
    ? decodeURIComponent(encodedName)
    : "课表.xlsx";
  triggerDownload(blob, filename);
}

export async function apiDownloadPlanningImportTemplate(
  projectId: string
): Promise<void> {
  const repository = await getProjectRepository();
  const result = await repository.planningImportTemplate(projectId);
  triggerDownload(result.blob, result.filename);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

interface DocumentRequest {
  projectId: string;
  commandType: string;
  payload: Record<string, unknown>;
}

function parseDocumentRequest(path: string, init?: RequestInit): DocumentRequest | null {
  const url = new URL(path, window.location.origin);
  const method = (init?.method ?? "GET").toUpperCase();
  const body = parseJsonBody(init?.body);
  const query = queryPayload(url);
  const courseMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/courses(?:\/([0-9a-f-]+))?$/i
  );
  if (courseMatch?.[1]) {
    if (!courseMatch[2] && method === "GET") {
      return documentCommand(courseMatch[1], "catalog.list_courses", {});
    }
    if (!courseMatch[2] && method === "POST") {
      return documentCommand(courseMatch[1], "catalog.add_course", body);
    }
    if (courseMatch[2] && method === "PATCH") {
      return documentCommand(courseMatch[1], "catalog.update_course", {
        ...body,
        course_id: courseMatch[2]
      });
    }
    if (courseMatch[2] && method === "DELETE") {
      return documentCommand(courseMatch[1], "catalog.delete_course", {
        ...query,
        course_id: courseMatch[2]
      });
    }
  }
  const roomMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/room-types(?:\/([0-9a-f-]+))?$/i
  );
  if (roomMatch?.[1]) {
    if (!roomMatch[2] && method === "GET") {
      return documentCommand(roomMatch[1], "catalog.list_rooms", {});
    }
    if (!roomMatch[2] && method === "POST") {
      return documentCommand(roomMatch[1], "catalog.add_room", body);
    }
    if (roomMatch[2] && method === "PATCH") {
      return documentCommand(roomMatch[1], "catalog.update_room", {
        ...body,
        room_type_id: roomMatch[2]
      });
    }
    if (roomMatch[2] && method === "DELETE") {
      return documentCommand(roomMatch[1], "catalog.delete_room", {
        ...query,
        room_type_id: roomMatch[2]
      });
    }
  }
  const catalogImportMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/catalog-imports$/i
  );
  if (catalogImportMatch?.[1] && method === "POST") {
    return documentCommand(
      catalogImportMatch[1],
      "catalog.import_rows",
      body
    );
  }
  const selectionMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/(teacher|class)-selection$/i
  );
  if (selectionMatch?.[1] && selectionMatch[2] && method === "PUT") {
    return documentCommand(
      selectionMatch[1],
      selectionMatch[2].toLowerCase() === "teacher"
        ? "catalog.sync_teachers"
        : "catalog.sync_classes",
      body
    );
  }
  const peopleMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/(teachers|classes)(?:\/([0-9a-f-]+))?$/i
  );
  if (peopleMatch?.[1] && peopleMatch[2]) {
    const isTeacher = peopleMatch[2].toLowerCase() === "teachers";
    if (!peopleMatch[3] && method === "GET") {
      return documentCommand(
        peopleMatch[1],
        isTeacher ? "catalog.list_teachers" : "catalog.list_classes",
        {}
      );
    }
    if (!peopleMatch[3] && method === "POST") {
      return documentCommand(
        peopleMatch[1],
        isTeacher ? "catalog.add_teacher" : "catalog.add_class",
        body
      );
    }
    if (peopleMatch[3] && method === "PATCH") {
      return documentCommand(
        peopleMatch[1],
        isTeacher ? "catalog.update_teacher" : "catalog.update_class",
        {
          ...body,
          [isTeacher ? "teacher_id" : "class_id"]: peopleMatch[3]
        }
      );
    }
    if (peopleMatch[3] && method === "DELETE") {
      return documentCommand(
        peopleMatch[1],
        isTeacher ? "catalog.delete_teacher" : "catalog.delete_class",
        {
          ...query,
          [isTeacher ? "teacher_id" : "class_id"]: peopleMatch[3]
        }
      );
    }
  }
  const demoDataMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/demo-data$/i
  );
  if (demoDataMatch?.[1] && method === "POST") {
    return documentCommand(
      demoDataMatch[1],
      "project.apply_demo_data",
      body
    );
  }
  const demoDataClearMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/demo-data\/(clear-preview|clear)$/i
  );
  if (demoDataClearMatch?.[1] && demoDataClearMatch[2] && method === "POST") {
    return documentCommand(
      demoDataClearMatch[1],
      demoDataClearMatch[2].toLowerCase() === "clear-preview"
        ? "project.preview_demo_clear"
        : "project.clear_demo_data",
      body
    );
  }
  const timeSlotMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/time-slots(?:\/([0-9a-f-]+))?$/i
  );
  if (timeSlotMatch?.[1]) {
    if (!timeSlotMatch[2] && method === "GET") {
      return documentCommand(timeSlotMatch[1], "calendar.list_time_slots", {});
    }
    if (!timeSlotMatch[2] && method === "POST") {
      return documentCommand(timeSlotMatch[1], "calendar.add_time_slot", body);
    }
    if (timeSlotMatch[2] && method === "PATCH") {
      return documentCommand(timeSlotMatch[1], "calendar.update_time_slot", {
        ...body,
        time_slot_id: timeSlotMatch[2]
      });
    }
    if (timeSlotMatch[2] && method === "DELETE") {
      return documentCommand(timeSlotMatch[1], "calendar.delete_time_slot", {
        ...query,
        time_slot_id: timeSlotMatch[2]
      });
    }
  }
  const unavailabilityMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/unavailability(?:\/cells)?$/i
  );
  if (unavailabilityMatch?.[1]) {
    if (method === "GET") {
      return documentCommand(
        unavailabilityMatch[1],
        "calendar.get_unavailability",
        query
      );
    }
    if (method === "PUT" && url.pathname.endsWith("/cells")) {
      return documentCommand(
        unavailabilityMatch[1],
        "calendar.set_unavailability",
        body
      );
    }
  }
  const courseTimeRuleMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/course-time-rules(?:\/([0-9a-f-]+))?$/i
  );
  if (courseTimeRuleMatch?.[1]) {
    if (!courseTimeRuleMatch[2] && method === "GET") {
      return documentCommand(
        courseTimeRuleMatch[1],
        "course_time_rules.list",
        {}
      );
    }
    if (!courseTimeRuleMatch[2] && method === "POST") {
      return documentCommand(
        courseTimeRuleMatch[1],
        "course_time_rules.add_non_adjacent",
        body
      );
    }
    if (courseTimeRuleMatch[2] && method === "PATCH") {
      return documentCommand(
        courseTimeRuleMatch[1],
        "course_time_rules.update_non_adjacent",
        { ...body, rule_id: courseTimeRuleMatch[2] }
      );
    }
    if (courseTimeRuleMatch[2] && method === "DELETE") {
      return documentCommand(
        courseTimeRuleMatch[1],
        "course_time_rules.delete_non_adjacent",
        { ...query, rule_id: courseTimeRuleMatch[2] }
      );
    }
  }
  const courseAdjacentRuleMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/course-adjacent-rules(?:\/([0-9a-f-]+))?$/i
  );
  if (courseAdjacentRuleMatch?.[1]) {
    if (!courseAdjacentRuleMatch[2] && method === "POST") {
      return documentCommand(
        courseAdjacentRuleMatch[1],
        "course_time_rules.add_adjacent",
        body
      );
    }
    if (courseAdjacentRuleMatch[2] && method === "PATCH") {
      return documentCommand(
        courseAdjacentRuleMatch[1],
        "course_time_rules.update_adjacent",
        { ...body, rule_id: courseAdjacentRuleMatch[2] }
      );
    }
    if (courseAdjacentRuleMatch[2] && method === "DELETE") {
      return documentCommand(
        courseAdjacentRuleMatch[1],
        "course_time_rules.delete_adjacent",
        { ...query, rule_id: courseAdjacentRuleMatch[2] }
      );
    }
  }
  const planningMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/planning(?:\/(.*))?$/i
  );
  if (planningMatch?.[1]) {
    const suffix = planningMatch[2] ?? "";
    if (!suffix && method === "GET") {
      return documentCommand(planningMatch[1], "planning.summary", {});
    }
    if (suffix === "draft" && method === "GET") {
      return documentCommand(planningMatch[1], "planning.get_draft", {});
    }
    if (suffix === "demo-data" && method === "POST") {
      return documentCommand(planningMatch[1], "planning.apply_demo_data", body);
    }
    if (suffix === "demo-data/clear-preview" && method === "POST") {
      return documentCommand(
        planningMatch[1],
        "planning.preview_demo_clear",
        body
      );
    }
    if (suffix === "demo-data/clear" && method === "POST") {
      return documentCommand(
        planningMatch[1],
        "planning.clear_demo_data",
        body
      );
    }
    if (suffix === "grade-expansions" && method === "POST") {
      return documentCommand(planningMatch[1], "planning.expand_grade", body);
    }
    if (suffix === "requirements" && method === "POST") {
      return documentCommand(planningMatch[1], "planning.add_requirement", body);
    }
    if (suffix === "requirements/batch" && method === "POST") {
      return documentCommand(
        planningMatch[1],
        "planning.add_requirements_batch",
        body
      );
    }
    if (suffix === "imports" && method === "POST") {
      return documentCommand(
        planningMatch[1],
        "planning.import_workbook",
        body
      );
    }
    if (suffix === "requirements/room-batch" && method === "PATCH") {
      return documentCommand(
        planningMatch[1],
        "planning.set_requirement_rooms",
        body
      );
    }
    const requirementMatch = suffix.match(/^requirements\/([0-9a-f-]+)$/i);
    if (requirementMatch?.[1] && method === "PATCH") {
      return documentCommand(planningMatch[1], "planning.update_requirement", {
        ...body,
        requirement_id: requirementMatch[1]
      });
    }
    if (requirementMatch?.[1] && method === "DELETE") {
      return documentCommand(planningMatch[1], "planning.delete_requirement", {
        ...query,
        requirement_id: requirementMatch[1]
      });
    }
    if (suffix === "allocations" && method === "POST") {
      return documentCommand(planningMatch[1], "planning.add_allocation", body);
    }
    const allocationMatch = suffix.match(/^allocations\/([0-9a-f-]+)$/i);
    if (allocationMatch?.[1] && method === "PATCH") {
      return documentCommand(planningMatch[1], "planning.update_allocation", {
        ...body,
        allocation_id: allocationMatch[1]
      });
    }
    if (allocationMatch?.[1] && method === "DELETE") {
      return documentCommand(planningMatch[1], "planning.delete_allocation", {
        ...query,
        allocation_id: allocationMatch[1]
      });
    }
    if (suffix === "teacher-expansions" && method === "POST") {
      return documentCommand(planningMatch[1], "planning.expand_teacher", body);
    }
    if (suffix === "preview" && method === "POST") {
      return documentCommand(planningMatch[1], "planning.preview", body);
    }
    if (suffix === "compile" && method === "POST") {
      return documentCommand(planningMatch[1], "planning.compile", body);
    }
    if (suffix === "reset" && method === "POST") {
      return documentCommand(planningMatch[1], "planning.reset", body);
    }
  }
  const planMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/plans(?:\/([0-9a-f-]+))?$/i
  );
  if (planMatch?.[1]) {
    if (!planMatch[2] && method === "GET") {
      return documentCommand(planMatch[1], "plans.list", {});
    }
    if (!planMatch[2] && method === "POST") {
      return documentCommand(planMatch[1], "plans.add", body);
    }
    if (planMatch[2] && method === "PATCH") {
      return documentCommand(planMatch[1], "plans.update", {
        ...body,
        plan_id: planMatch[2]
      });
    }
    if (planMatch[2] && method === "DELETE") {
      return documentCommand(planMatch[1], "plans.delete", {
        ...query,
        plan_id: planMatch[2]
      });
    }
  }
  const planBatchMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/plans\/batch$/i
  );
  if (planBatchMatch?.[1] && method === "POST") {
    return documentCommand(planBatchMatch[1], "plans.add_batch", body);
  }
  const schedulingMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/scheduling\/(preflight|generate)$/i
  );
  if (schedulingMatch?.[1] && schedulingMatch[2] && method === "POST") {
    return documentCommand(
      schedulingMatch[1],
      `scheduling.${schedulingMatch[2].toLowerCase()}`,
      body
    );
  }
  const timetableViewMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/timetables\/(classes|teachers|rooms)$/i
  );
  if (timetableViewMatch?.[1] && timetableViewMatch[2] && method === "GET") {
    const segment = timetableViewMatch[2].toLowerCase();
    return documentCommand(timetableViewMatch[1], "timetable.view", {
      view: segment === "classes" ? "CLASS" : segment === "teachers" ? "TEACHER" : "ROOM",
      object_id: query.class_id ?? query.teacher_id ?? query.room_id ?? null,
      week_parity: query.week_parity ?? "ODD"
    });
  }
  const lessonMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/timetable\/lessons(?:\/([0-9a-f-]+))?$/i
  );
  if (lessonMatch?.[1]) {
    if (!lessonMatch[2] && method === "POST") {
      return documentCommand(lessonMatch[1], "timetable.add_lesson", body);
    }
    if (lessonMatch[2] && method === "PATCH") {
      return documentCommand(lessonMatch[1], "timetable.update_lesson", {
        ...body,
        lesson_id: lessonMatch[2]
      });
    }
    if (lessonMatch[2] && method === "DELETE") {
      return documentCommand(lessonMatch[1], "timetable.delete_lesson", {
        ...query,
        lesson_id: lessonMatch[2]
      });
    }
  }
  const timetableActionMatch = url.pathname.match(
    /^\/projects\/([0-9a-f-]+)\/timetable\/(drag-options|moves|swaps)$/i
  );
  if (timetableActionMatch?.[1] && timetableActionMatch[2] && method === "POST") {
    const commandType = {
      "drag-options": "timetable.drag_options",
      moves: "timetable.move",
      swaps: "timetable.swap"
    }[timetableActionMatch[2].toLowerCase()];
    if (commandType) {
      return documentCommand(timetableActionMatch[1], commandType, body);
    }
  }
  return null;
}

function documentCommand(
  projectId: string,
  commandType: string,
  payload: Record<string, unknown>
): DocumentRequest {
  return { projectId, commandType, payload };
}

function queryPayload(url: URL): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  url.searchParams.forEach((value, key) => {
    if (key.endsWith("revision")) payload[key] = Number(value);
    else if (value === "true" || value === "false") payload[key] = value === "true";
    else payload[key] = value;
  });
  return payload;
}

function parseJsonBody(body: BodyInit | null | undefined): Record<string, unknown> {
  if (body === undefined || body === null) return {};
  if (typeof body !== "string") {
    throw new Error("项目命令正文必须使用 JSON 字符串");
  }
  const parsed: unknown = JSON.parse(body);
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("项目命令正文必须是 JSON 对象");
  }
  return parsed as Record<string, unknown>;
}
